import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Typography, useTheme, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, IconButton
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Header from "../../components/Header";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const Team = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal création admin
  const [openModal, setOpenModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    email: "",
    password: "",
    role: "admin"
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken }),
      });
      const data = await res.json();
      if (data.success) {
        setAdmins(data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur de chargement des administrateurs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/team/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, ...newAdmin }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Nouvel administrateur créé !");
        setOpenModal(false);
        setNewAdmin({ username: "", email: "", password: "", role: "admin" });
        fetchTeam();
      } else {
        toast.error(data.message || "Erreur de création");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet administrateur ?")) return;
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/team/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken, id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Administrateur supprimé !");
        fetchTeam();
      } else {
        toast.error(data.message || "Erreur lors de la suppression");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.8 },
    {
      field: "username",
      headerName: "Nom d'utilisateur",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Typography fontWeight="bold" color={colors.grey[100]}>
          {params.value}
        </Typography>
      ),
    },
    { field: "email", headerName: "Email", flex: 1.2 },
    {
      field: "created_at",
      headerName: "Date de Création",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[200]}>
          {params.value ? new Date(params.value).toLocaleDateString("fr-FR", {
            day: "2-digit", month: "2-digit", year: "numeric"
          }) : "-"}
        </Typography>
      ),
    },
    {
      field: "role",
      headerName: "Niveau d'Accès",
      flex: 1,
      renderCell: ({ row: { role } }) => {
        const isSuper = role === "superadmin";
        const isAdmin = role === "admin";
        return (
          <Box
            width="80%"
            m="0 auto"
            p="5px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            backgroundColor={
              isSuper
                ? colors.greenAccent[600]
                : isAdmin
                ? colors.blueAccent[600]
                : colors.primary[500]
            }
            borderRadius="4px"
          >
            {isSuper && <AdminPanelSettingsOutlinedIcon />}
            {isAdmin && <SecurityOutlinedIcon />}
            {!isSuper && !isAdmin && <LockOpenOutlinedIcon />}
            <Typography color={colors.grey[100]} sx={{ ml: "5px", textTransform: "capitalize" }}>
              {role || "admin"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      renderCell: ({ row: { id } }) => (
        <IconButton onClick={() => handleDeleteAdmin(id)} sx={{ color: "#d32f2f" }}>
          <DeleteOutlineIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px">
        <Header title="ÉQUIPE D'ADMINISTRATION" subtitle="Gestion des accès au tableau de bord LinkerVTC" />
        <Button
          onClick={() => setOpenModal(true)}
          variant="contained"
          startIcon={<PersonAddAlt1Icon />}
          sx={{
            backgroundColor: colors.greenAccent[600],
            color: "#fff",
            fontWeight: "bold",
            padding: "10px 20px",
            "&:hover": { backgroundColor: colors.greenAccent[700] },
          }}
        >
          Ajouter un Administrateur
        </Button>
      </Box>

      <Box
        m="30px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
        }}
      >
        <DataGrid rows={admins} columns={columns} loading={loading} />
      </Box>

      {/* MODAL CRÉATION NOUVEL ADMIN */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleAddAdmin}>
          <DialogTitle sx={{ backgroundColor: colors.primary[400], color: colors.grey[100] }}>
            Ajouter un Administrateur
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: colors.primary[400], pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Nom d'utilisateur"
              fullWidth
              required
              value={newAdmin.username}
              onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
              sx={{ mt: 1 }}
            />
            <TextField
              label="Adresse Email"
              type="email"
              fullWidth
              required
              value={newAdmin.email}
              onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
            />
            <TextField
              label="Mot de passe"
              type="password"
              fullWidth
              required
              value={newAdmin.password}
              onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
            />
            <TextField
              select
              label="Rôle / Permissions"
              fullWidth
              value={newAdmin.role}
              onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
            >
              <MenuItem value="superadmin">Super Administrateur (Tous les droits)</MenuItem>
              <MenuItem value="admin">Administrateur Standard</MenuItem>
              <MenuItem value="support">Support Opérationnel</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: colors.primary[400], p: 2 }}>
            <Button onClick={() => setOpenModal(false)} color="inherit">Annuler</Button>
            <Button type="submit" variant="contained" disabled={submitting} sx={{ backgroundColor: colors.greenAccent[600] }}>
              {submitting ? "Création..." : "Créer le compte"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Team;
